if (!customElements.get('mega-menu')) {
  class MegaMenu extends HTMLElement {
    // musi odpowiadac transition na .drawer__content w component-drawer.css
    static CLOSE_DURATION = 400;

    constructor() {
      super();

      this.selectors = {
        contents: this.querySelectorAll('.drawer__content'),
        next: this.querySelectorAll('[aria-controls][aria-expanded]'),
        back: this.querySelectorAll('.menu__header-back'),
        menus: this.querySelectorAll('[data-menu]'),
        openTriggers: document.querySelectorAll('[data-megamenu-open-handle]'),
      };

      this.selectors.next.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          this.expandFromTrigger(trigger);
        });
      });

      this.selectors.openTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          const handle = trigger.getAttribute('data-megamenu-open-handle');

          if (!this.hasPrimaryMenu(handle)) return;

          if (!this.openInitialMenu(handle, trigger)) return;

          event.preventDefault();
          window.dispatchEvent(new CustomEvent('drawerOpen', { detail: { id: 'drawer-menu' } }));
        });
      });

      this.selectors.back.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const content = trigger.closest('.drawer__content');
          const handle = trigger.getAttribute('data-handle');

          content.classList.remove('drawer__content--expanded');
          this.close(handle);
          this.selectors.next.forEach((nextTrigger) => {
            if (nextTrigger.getAttribute('aria-controls') === content.id) {
              this.setTriggerExpanded(nextTrigger, false);
            }
          });
          window.dispatchEvent(new CustomEvent('drawerResize'));
        });
      });

      this.selectors.contents.forEach((content) => {
        content.addEventListener('click', (event) => event.stopPropagation());
      });

      window.addEventListener('drawerClosed', (event) => {
        if (event.detail && event.detail.id !== 'drawer-menu') return;
        this.scheduleReset();
      });
    }

    // Zamkniecie szuflady rozklada sie na dwa momenty, bo dwie rzeczy dzieja sie
    // w roznym tempie:
    //   1) OD RAZU zdejmujemy klase rozwiniecia z paneli. Na desktopie panel
    //      rozwiniety ma transform: translate(0) !important, wiec dopoki klasa
    //      wisi, panel stoi nieruchomo i po prostu znika — zamiast wyjechac.
    //   2) PO animacji (0.4 s) chowamy zawartosc i czyscimy reszte stanu.
    //      Zrobione od razu opróznialoby panel w trakcie ruchu.
    // Ponowne otwarcie w tym oknie anuluje krok 2.
    scheduleReset() {
      this.cancelScheduledReset();
      this.collapsePanels();
      this.resetTimer = setTimeout(() => {
        this.resetTimer = null;
        const drawer = this.querySelector('#drawer-menu');
        if (drawer && drawer.classList.contains('drawer--opened')) return;
        this.reset();
      }, MegaMenu.CLOSE_DURATION);
    }

    collapsePanels() {
      this.selectors.contents.forEach((content) => {
        content.classList.remove('drawer__content--expanded');
      });
      this.selectors.next.forEach((trigger) => {
        this.setTriggerExpanded(trigger, false);
      });
      this.selectors.openTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    cancelScheduledReset() {
      if (this.resetTimer) {
        clearTimeout(this.resetTimer);
        this.resetTimer = null;
      }
    }

    expandFromTrigger(trigger) {
      const contentId = trigger.getAttribute('aria-controls');
      const level = this.getLevel(contentId);
      const handle = trigger.getAttribute('data-handle');

      this.close(handle);
      this.open(handle);
      this.setTriggerExpanded(trigger, true);
      this.selectors.contents.forEach((content) => {
        if (this.getLevel(content.id) > level) {
          content.classList.remove('drawer__content--expanded');
          content.querySelectorAll('[data-menu]').forEach((menu) => this.hideMenu(menu));
          this.selectors.next.forEach((nextTrigger) => {
            if (nextTrigger.getAttribute('aria-controls') === content.id) {
              this.setTriggerExpanded(nextTrigger, false);
            }
          });
        }
      });

      const content = this.querySelector(`#${contentId}`);
      if (content) content.classList.add('drawer__content--expanded');

      window.dispatchEvent(new CustomEvent('drawerResize'));
    }

    openInitialMenu(handle, trigger) {
      this.cancelScheduledReset();
      this.reset();

      const menuTrigger = Array.from(this.selectors.next).find((nextTrigger) => {
        return (
          nextTrigger.getAttribute('data-handle') === handle &&
          nextTrigger.getAttribute('aria-controls') === 'drawer-content-2'
        );
      });

      if (!menuTrigger) return false;

      this.expandFromTrigger(menuTrigger);
      trigger.setAttribute('aria-expanded', 'true');

      return true;
    }

    hasPrimaryMenu(handle) {
      return Array.from(this.selectors.next).some((trigger) => {
        return (
          trigger.getAttribute('data-handle') === handle &&
          trigger.getAttribute('aria-controls') === 'drawer-content-2'
        );
      });
    }

    getLevel(contentId) {
      return parseInt(contentId.split('-').slice(-1)[0], 10);
    }

    open(handle) {
      this.selectors.menus.forEach((menu) => {
        if (menu.getAttribute('data-menu') === handle) {
          menu.style.display = 'block';
          menu.style.opacity = '1';
        }
      });
    }

    close(handle) {
      this.selectors.menus.forEach((menu) => {
        if (menu.getAttribute('data-menu') !== handle) return;

        const content = menu.closest('.drawer__content');
        if (!content) return;

        content.querySelectorAll('[data-menu]').forEach((relatedMenu) => {
          const relatedHandle = relatedMenu.getAttribute('data-menu');

          if (relatedHandle === handle) return;

          this.hideMenu(relatedMenu);
          this.selectors.next.forEach((trigger) => {
            if (trigger.getAttribute('data-handle') === relatedHandle) {
              this.setTriggerExpanded(trigger, false);
            }
          });
        });
      });
    }

    hideMenu(menu) {
      menu.style.display = 'none';
      menu.style.opacity = '0';
      menu.querySelectorAll('[aria-expanded]').forEach((trigger) => {
        this.setTriggerExpanded(trigger, false);
      });
    }

    setTriggerExpanded(trigger, expanded) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      trigger.classList.toggle('selected', expanded);
    }

    reset() {
      this.collapsePanels();
      const drawer = this.querySelector('#drawer-menu');
      if (drawer) drawer.classList.remove('drawer--instant-open');
      this.selectors.menus.forEach((menu) => {
        this.hideMenu(menu);
      });
    }
  }

  customElements.define('mega-menu', MegaMenu);
}
